import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Camera, Upload, X, Loader2, ImageIcon } from "lucide-react";
import { Button } from "@tabipla/ui";
import { resizeImage } from "@tabipla/ui/lib/image-resize";
import { apiClient } from "../../../infrastructure/api/client";

export type GroupImageType = "group_icon" | "group_cover" | "group_background";

interface GroupImageUploaderProps {
  /** Current image URL */
  imageUrl: string | null;
  /** The type of image being uploaded */
  imageType: GroupImageType;
  /** Callback when image is uploaded */
  onUpload: (url: string, key: string) => void;
  /** Callback when image is removed */
  onRemove: () => void;
  /** Whether uploading is in progress */
  isUploading?: boolean;
  /** Whether the uploader is disabled */
  disabled?: boolean;
  /** Label text */
  label?: string;
  /** Preview size (default: "md") */
  size?: "sm" | "md" | "lg";
  /** Shape of the preview */
  shape?: "square" | "circle" | "banner";
}

export function GroupImageUploader({
  imageUrl,
  imageType,
  onUpload,
  onRemove,
  isUploading = false,
  disabled = false,
  label,
  size = "md",
  shape = "square",
}: GroupImageUploaderProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const isProcessing = isUploading || uploading;

  const handleFileSelect = useCallback(
    async (file: File) => {
      if (!file.type.startsWith("image/")) {
        return;
      }

      // Show preview
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setUploading(true);

      try {
        // Resize image before upload
        const resizedFile = await resizeImage(file);

        // Upload to server
        const formData = new FormData();
        formData.append("file", resizedFile);
        formData.append("type", imageType);

        const response = await apiClient.uploadFormData<{ key: string; url: string }>(
          "/api/upload",
          formData
        );

        if (response.success && response.data) {
          onUpload(response.data.url, response.data.key);
        }
      } finally {
        URL.revokeObjectURL(url);
        setPreviewUrl(null);
        setUploading(false);
      }
    },
    [imageType, onUpload]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    e.target.value = "";
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const displayUrl = previewUrl || imageUrl;

  // Size classes
  const sizeClasses = {
    sm: "h-16 w-16",
    md: "h-24 w-24",
    lg: "h-32 w-32",
  };

  // Shape classes
  const shapeClasses = {
    square: "rounded-xl",
    circle: "rounded-full",
    banner: "rounded-xl aspect-[16/9] w-full h-auto",
  };

  const previewSize = shape === "banner" ? "w-full" : sizeClasses[size];
  const previewShape = shapeClasses[shape];

  return (
    <div className="space-y-3">
      {label && <label className="text-sm font-medium">{label}</label>}

      {/* Current image or upload area */}
      {displayUrl ? (
        <div className="relative inline-block">
          <img
            src={displayUrl}
            alt="Preview"
            className={`${previewSize} ${previewShape} border object-cover shadow-sm ${previewUrl ? "opacity-50" : ""}`}
          />
          {isProcessing && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
          {!isProcessing && !disabled && (
            <button
              type="button"
              onClick={onRemove}
              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-white shadow-md transition-transform hover:scale-110"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ) : (
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative ${previewSize} ${previewShape} border-2 border-dashed transition-all ${
            dragActive
              ? "border-primary bg-primary/5"
              : "border-muted-foreground/25 hover:border-muted-foreground/50"
          } ${disabled || isProcessing ? "pointer-events-none opacity-50" : ""}`}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleInputChange}
            className="hidden"
            disabled={disabled || isProcessing}
          />

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isProcessing}
            className="flex h-full w-full flex-col items-center justify-center gap-1"
          >
            {isProcessing ? (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            ) : (
              <ImageIcon className="h-6 w-6 text-muted-foreground" />
            )}
            {shape !== "banner" && (
              <span className="text-xs text-muted-foreground">{t("groups.addImage", "追加")}</span>
            )}
          </button>
        </div>
      )}

      {/* Upload buttons for banner shape */}
      {shape === "banner" && !displayUrl && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isProcessing}
            className="gap-1.5"
          >
            <Camera className="h-4 w-4" />
            {t("groups.camera", "カメラ")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.removeAttribute("capture");
                fileInputRef.current.click();
                setTimeout(() => {
                  fileInputRef.current?.setAttribute("capture", "environment");
                }, 100);
              }
            }}
            disabled={disabled || isProcessing}
            className="gap-1.5"
          >
            <Upload className="h-4 w-4" />
            {t("groups.gallery", "ギャラリー")}
          </Button>
        </div>
      )}
    </div>
  );
}
